import React, { useMemo } from "react";
import {
	ActionTargetLabels,
	ActionType,
	ActionTypeLabels,
	ConditionOperatorLabels,
	ConditionSubjectLabels,
	OfferActionField,
	OfferChannelLabels,
	OfferConditionField,
	OfferField,
	OfferFormMode,
	OfferMappingField,
	OfferRowChange,
	OfferRuleField,
	OfferTermsBlockField,
	TermsConditionType,
	TermsConditionTypeLabels,
	WIZARD_STEPS,
} from "../../constants";
import {
	formatDate,
	formatMappingDisplay,
	getListItems,
	getParagraphText,
	isBlank,
} from "../../utils/offerDisplay";
import { diffOfferDraft } from "../../utils/offerDraftDiff";
import {
	OverviewBanner,
	OverviewCard,
	OverviewCardHeader,
	OverviewCardTitle,
	OverviewEditLink,
	OverviewField,
	OverviewFieldGrid,
	OverviewFieldLabel,
	OverviewFieldValue,
	OverviewIntro,
	OverviewNote,
	OverviewRemovedTag,
	OverviewStack,
	OverviewSubtitle,
	OverviewTable,
	OverviewTitle,
} from "./OfferFormLayout";

const EMPTY_SELECTED = "Not selected";
const EMPTY_PROVIDED = "Not provided";

/** Wraps a selected-field value with overview empty copy. */
const formatSelectedValue = (value, labels = {}) => {
	if (isBlank(value)) return { text: EMPTY_SELECTED, muted: true };
	const label = labels[value];
	return { text: label || String(value), muted: false };
};

/** Wraps a free-text/number value with overview empty copy. */
const formatProvidedValue = (value) => {
	if (isBlank(value)) return { text: EMPTY_PROVIDED, muted: true };
	return { text: String(value), muted: false };
};

/** Formats a date for overview, using "Not provided" when empty. */
const formatDateValue = (value) => {
	if (!value) return { text: EMPTY_PROVIDED, muted: true };
	const formatted = formatDate(value);
	if (!formatted || formatted === "-") return { text: EMPTY_PROVIDED, muted: true };
	return { text: formatted, muted: false };
};

/** Formats a mapping field for overview cards. */
const formatMappingSummary = (value, labels = {}) => ({
	text: formatMappingDisplay(value, { labels }),
	muted: false,
});

/** Formats an action value, appending % for percent discounts. */
const formatActionValue = (type, value) => {
	if (isBlank(value)) return { text: EMPTY_PROVIDED, muted: true };
	if (type === ActionType.PERCENT) return { text: `${value}%`, muted: false };
	return { text: String(value), muted: false };
};

/** Formats min/max discount limits for overview. */
const formatAmountLimit = (value, { isMin = false } = {}) => {
	if (isBlank(value)) return { text: EMPTY_PROVIDED, muted: true };
	const numeric = Number(value);
	if (isMin && (numeric === 0 || value === "0")) {
		return { text: "0 (No Minimum)", muted: false };
	}
	return { text: `${value}`, muted: false };
};

/** Formats a condition value, preferring the dish label when present. */
const formatConditionValue = (condition) => {
	const value = condition?.[OfferConditionField.VALUE];
	const label = condition?.[OfferConditionField.VALUE_LABEL];
	if (isBlank(value)) return { text: EMPTY_SELECTED, muted: true };
	if (label) return { text: String(label), muted: false };
	return { text: String(value), muted: false };
};

/** Formats terms content for overview, including numbered list items. */
const formatTermsContent = (block) => {
	const type = block?.[OfferTermsBlockField.TYPE];
	const content = block?.[OfferTermsBlockField.CONTENT];
	if (type === TermsConditionType.ORDERED_LIST) {
		const items = getListItems(content);
		if (!items.length) return { text: EMPTY_PROVIDED, muted: true };
		return { text: items.map((item, index) => `${index + 1}. ${item}`).join("\n"), muted: false };
	}
	const text = getParagraphText(content);
	if (!text) return { text: EMPTY_PROVIDED, muted: true };
	return { text, muted: false };
};

const FieldItem = ({ label, display }) => (
	<OverviewField>
		<OverviewFieldLabel>{label}</OverviewFieldLabel>
		<OverviewFieldValue $muted={display.muted}>{display.text}</OverviewFieldValue>
	</OverviewField>
);

const SectionCard = ({ title, onEdit, children }) => (
	<OverviewCard>
		<OverviewCardHeader>
			<OverviewCardTitle>{title}</OverviewCardTitle>
			{onEdit ? (
				<OverviewEditLink type="button" onClick={onEdit}>
					Edit
				</OverviewEditLink>
			) : null}
		</OverviewCardHeader>
		{children}
	</OverviewCard>
);

const OfferSummary = ({
	draft,
	baseline = null,
	mode = OfferFormMode.CREATE,
	offerGroups = [],
	onEditSection,
}) => {
	const isEditMode = mode === OfferFormMode.EDIT;
	const diff = useMemo(
		() => (isEditMode ? diffOfferDraft(baseline, draft) : null),
		[isEditMode, baseline, draft]
	);

	const offerGroupName = useMemo(() => {
		const groupId = draft?.[OfferField.OFFER_GROUP_ID];
		if (!groupId) return "";
		const match = (offerGroups || []).find((g) => String(g._id || g.id) === String(groupId));
		return match?.name || "";
	}, [draft, offerGroups]);

	const rule = draft?.[OfferField.RULE] || {};
	const action = rule[OfferRuleField.ACTION] || {};
	const mapping = draft?.[OfferField.OFFER_MAPPING] || {};
	const conditions = rule[OfferRuleField.CONDITIONS] || [];
	const terms = draft?.[OfferField.TERMS_CONDITION] || [];

	const showBasicField = (field) => !isEditMode || Object.prototype.hasOwnProperty.call(diff.basic, field);
	const showRuleField = (field) =>
		!isEditMode || Object.prototype.hasOwnProperty.call(diff.rules.fields, field);
	const showActionField = (field) =>
		!isEditMode || Object.prototype.hasOwnProperty.call(diff.action, field);
	const showMappingField = (field) =>
		!isEditMode || Object.prototype.hasOwnProperty.call(diff.mapping, field);

	const conditionRows = isEditMode
		? diff.rules.conditions.map((row) => ({
				index: row.index,
				change: row.change,
				condition:
					row.change === OfferRowChange.REMOVED
						? row.baselineValue || row.value
						: row.value,
			}))
		: conditions.map((condition, index) => ({
				index,
				change: null,
				condition,
			}));

	const termsRows = isEditMode
		? diff.terms.map((row) => ({
				index: row.index,
				change: row.change,
				block:
					row.change === OfferRowChange.REMOVED
						? row.baselineValue || row.value
						: row.value,
			}))
		: terms.map((block, index) => ({
				index,
				change: null,
				block,
			}));

	const showBasicCard = !isEditMode || Object.keys(diff.basic).length > 0;
	const showRulesCard = !isEditMode || Object.keys(diff.rules.fields).length > 0 || diff.rules.conditions.length > 0;
	const showActionCard = !isEditMode || Object.keys(diff.action).length > 0;
	const showMappingCard = !isEditMode || Object.keys(diff.mapping).length > 0;
	const showTermsCard = !isEditMode || diff.terms.length > 0;

	const handleEdit = (step) => {
		onEditSection?.(step);
	};

	return (
		<OverviewStack>
			<OverviewIntro>
				<OverviewTitle>Offer Overview</OverviewTitle>
				<OverviewSubtitle>
					{isEditMode
						? "Review your changes before saving."
						: "Review every field before creating your offer."}
				</OverviewSubtitle>
			</OverviewIntro>

			<OverviewBanner>
				<span className="material-symbols-outlined" style={{ fontSize: "1.1rem" }}>
					info
				</span>
				<span>
					{isEditMode
						? "Only changed fields are shown."
						: "All fields are shown, including empty and unselected values."}
				</span>
			</OverviewBanner>

			{showBasicCard ? (
				<SectionCard title="Basic Information" onEdit={() => handleEdit(WIZARD_STEPS.BASIC)}>
					<OverviewFieldGrid>
						{showBasicField(OfferField.TITLE) ? (
							<FieldItem
								label="Offer Title"
								display={formatProvidedValue(draft[OfferField.TITLE])}
							/>
						) : null}
						{showBasicField(OfferField.OFFER_CODE) ? (
							<FieldItem
								label="Offer Code"
								display={formatProvidedValue(draft[OfferField.OFFER_CODE])}
							/>
						) : null}
						{showBasicField(OfferField.OFFER_TYPE) ? (
							<FieldItem
								label="Offer Type"
								display={formatSelectedValue(draft[OfferField.OFFER_TYPE])}
							/>
						) : null}
						{showBasicField(OfferField.SUB_OFFER_TYPE) ? (
							<FieldItem
								label="Sub-offer Type"
								display={formatSelectedValue(draft[OfferField.SUB_OFFER_TYPE])}
							/>
						) : null}
						{showBasicField(OfferField.DESCRIPTION) ? (
							<FieldItem
								label="Description"
								display={formatProvidedValue(draft[OfferField.DESCRIPTION])}
							/>
						) : null}
						{showBasicField(OfferField.OFFER_GROUP_ID) ? (
							<FieldItem
								label="Offer Group"
								display={formatSelectedValue(
									draft[OfferField.OFFER_GROUP_ID],
									offerGroupName
										? { [draft[OfferField.OFFER_GROUP_ID]]: offerGroupName }
										: {}
								)}
							/>
						) : null}
						{showBasicField(OfferField.PRIORITY) ? (
							<FieldItem
								label="Priority"
								display={formatSelectedValue(draft[OfferField.PRIORITY])}
							/>
						) : null}
						{showBasicField(OfferField.CHANNEL) ? (
							<FieldItem
								label="Channel"
								display={formatSelectedValue(
									draft[OfferField.CHANNEL],
									OfferChannelLabels
								)}
							/>
						) : null}
						{showBasicField(OfferField.START_DATE) ? (
							<FieldItem
								label="Start Date"
								display={formatDateValue(draft[OfferField.START_DATE])}
							/>
						) : null}
						{showBasicField(OfferField.END_DATE) ? (
							<FieldItem
								label="End Date"
								display={formatDateValue(draft[OfferField.END_DATE])}
							/>
						) : null}
						{showBasicField(OfferField.MAX_USAGE_COUNT) ? (
							<FieldItem
								label="Maximum Usage Count"
								display={formatProvidedValue(draft[OfferField.MAX_USAGE_COUNT])}
							/>
						) : null}
						{showBasicField(OfferField.INDIVIDUAL_MAX_USAGE_COUNT) ? (
							<FieldItem
								label="Per-user Usage Limit"
								display={formatProvidedValue(
									draft[OfferField.INDIVIDUAL_MAX_USAGE_COUNT]
								)}
							/>
						) : null}
					</OverviewFieldGrid>
				</SectionCard>
			) : null}

			{showRulesCard ? (
				<SectionCard
					title="Rules & Conditions"
					onEdit={() => handleEdit(WIZARD_STEPS.RULES)}>
					<OverviewFieldGrid>
						{showRuleField(OfferRuleField.DESCRIPTION) ? (
							<FieldItem
								label="Rule Description"
								display={formatProvidedValue(rule[OfferRuleField.DESCRIPTION])}
							/>
						) : null}
						{showRuleField(OfferRuleField.OPERATOR) ? (
							<FieldItem
								label="Combine Conditions With"
								display={formatSelectedValue(rule[OfferRuleField.OPERATOR])}
							/>
						) : null}
					</OverviewFieldGrid>

					{conditionRows.length > 0 ? (
						<>
							<OverviewTable>
								<thead>
									<tr>
										<th>#</th>
										<th>Subject</th>
										<th>Operator</th>
										<th>Value</th>
									</tr>
								</thead>
								<tbody>
									{conditionRows.map((row) => {
										const subject = formatSelectedValue(
											row.condition?.[OfferConditionField.SUBJECT],
											ConditionSubjectLabels
										);
										const operator = formatSelectedValue(
											row.condition?.[OfferConditionField.OPERATOR],
											ConditionOperatorLabels
										);
										const value = formatConditionValue(row.condition);
										return (
											<tr key={`condition-${row.index}-${row.change || "view"}`}>
												<td>
													{row.index + 1}
													{row.change === OfferRowChange.REMOVED ? (
														<OverviewRemovedTag>Removed</OverviewRemovedTag>
													) : null}
												</td>
												<td>
													<OverviewFieldValue $muted={subject.muted}>
														{subject.text}
													</OverviewFieldValue>
												</td>
												<td>
													<OverviewFieldValue $muted={operator.muted}>
														{operator.text}
													</OverviewFieldValue>
												</td>
												<td>
													<OverviewFieldValue $muted={value.muted}>
														{value.text}
													</OverviewFieldValue>
												</td>
											</tr>
										);
									})}
								</tbody>
							</OverviewTable>
							<OverviewNote>
								All conditions must be true for the rule to apply when combined with
								AND.
							</OverviewNote>
						</>
					) : null}
				</SectionCard>
			) : null}

			{showActionCard ? (
				<SectionCard title="Action" onEdit={() => handleEdit(WIZARD_STEPS.RULES)}>
					<OverviewFieldGrid>
						{showActionField(OfferActionField.TYPE) ? (
							<FieldItem
								label="Action Type"
								display={formatSelectedValue(
									action[OfferActionField.TYPE],
									ActionTypeLabels
								)}
							/>
						) : null}
						{showActionField(OfferActionField.VALUE) ? (
							<FieldItem
								label="Action Value"
								display={formatActionValue(
									action[OfferActionField.TYPE],
									action[OfferActionField.VALUE]
								)}
							/>
						) : null}
						{showActionField(OfferActionField.TARGET) ? (
							<FieldItem
								label="Apply Discount On"
								display={formatSelectedValue(
									action[OfferActionField.TARGET],
									ActionTargetLabels
								)}
							/>
						) : null}
						{showActionField(OfferActionField.MAX_AMOUNT_LIMIT) ? (
							<FieldItem
								label="Max Discount Limit"
								display={formatAmountLimit(action[OfferActionField.MAX_AMOUNT_LIMIT])}
							/>
						) : null}
						{showActionField(OfferActionField.MIN_AMOUNT_LIMIT) ? (
							<FieldItem
								label="Min Discount Limit"
								display={formatAmountLimit(action[OfferActionField.MIN_AMOUNT_LIMIT], {
									isMin: true,
								})}
							/>
						) : null}
					</OverviewFieldGrid>
				</SectionCard>
			) : null}

			{showMappingCard ? (
				<SectionCard title="Mapping" onEdit={() => handleEdit(WIZARD_STEPS.MAPPING)}>
					<OverviewFieldGrid>
						{showMappingField(OfferMappingField.MENU_IDS) ? (
							<FieldItem
								label="Menus"
								display={formatMappingSummary(
									mapping[OfferMappingField.MENU_IDS],
									draft[OfferField.MENU_LABELS]
								)}
							/>
						) : null}
						{showMappingField(OfferMappingField.RESTAURANT_IDS) ? (
							<FieldItem
								label="Restaurants"
								display={formatMappingSummary(
									mapping[OfferMappingField.RESTAURANT_IDS]
								)}
							/>
						) : null}
						{showMappingField(OfferMappingField.USER_IDS) ? (
							<FieldItem
								label="Users"
								display={formatMappingSummary(mapping[OfferMappingField.USER_IDS])}
							/>
						) : null}
					</OverviewFieldGrid>
				</SectionCard>
			) : null}

			{showTermsCard ? (
				<SectionCard
					title="Terms & Conditions"
					onEdit={() => handleEdit(WIZARD_STEPS.TERMS)}>
					{termsRows.length === 0 ? (
						<OverviewFieldValue $muted>Not provided</OverviewFieldValue>
					) : (
						<OverviewFieldGrid>
							{termsRows.map((row) => {
								const type = formatSelectedValue(
									row.block?.[OfferTermsBlockField.TYPE],
									TermsConditionTypeLabels
								);
								const content = formatTermsContent(row.block);
								return (
									<React.Fragment key={`terms-${row.index}-${row.change || "view"}`}>
										<OverviewField>
											<OverviewFieldLabel>
												Block {row.index + 1} Type
												{row.change === OfferRowChange.REMOVED ? (
													<OverviewRemovedTag>Removed</OverviewRemovedTag>
												) : null}
											</OverviewFieldLabel>
											<OverviewFieldValue $muted={type.muted}>
												{type.text}
											</OverviewFieldValue>
										</OverviewField>
										<OverviewField style={{ gridColumn: "span 2" }}>
											<OverviewFieldLabel>Content</OverviewFieldLabel>
											<OverviewFieldValue
												$muted={content.muted}
												style={{ whiteSpace: "pre-wrap" }}>
												{content.text}
											</OverviewFieldValue>
										</OverviewField>
									</React.Fragment>
								);
							})}
						</OverviewFieldGrid>
					)}
				</SectionCard>
			) : null}
		</OverviewStack>
	);
};

export default OfferSummary;
